import { array, bool, func, number, object, string } from 'prop-types';
import React, { useEffect } from 'react';
import _chunk from 'lodash/chunk';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import styled, { withTheme } from 'styled-components';

import Badge from 'ravenjs/lib/Badge';
import { callFunc } from 'ravenjs/utils/actions';
import Col from 'ravenjs/lib/Col';
import { get } from 'ravenjs/utils/lodash';
import Row from 'ravenjs/lib/Row';
import Typography from 'ravenjs/lib/Typography';
import Checkbox from 'ravenjs/lib/CheckboxNew';
import { addEventListenerOnRoot, removeEventListenerOnRoot } from 'ravenjs/utils/events';
import { themeGet, getThemeProps } from 'ravenjs/utils/theme';

import ContentTooltipPreview from 'components/ContentTooltipPreview';
import { CheckboxEmptyIcon, CheckboxFilledIcon, GridIcon } from 'components/Icons';

import { DEFAULT_COLORS } from 'constants/colors';
import { FILTER_DOCUMENT_TYPES } from 'constants/content';
import { TOOLTIP_FILTER_ALL_BADGE } from 'constants/filters';

const TooltipFilterWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const HeaderStyled = styled.div`
    display: flex;
    justify-content: space-between;
    position: relative;
    align-items: center;
    background-color: ${DEFAULT_COLORS.WHITE};
    border: solid 1px ${DEFAULT_COLORS.PLACEHOLDER};
    width: 100%;
    height: 2.375rem;
    padding: 0.5625rem 0.8125rem;
    border-radius: 2px;
    &.disabled {
        background-color: ${themeGet('palette.common.inputDisabled')} !important;
        border-color: transparent;
        box-shadow: none;
        cursor: not-allowed;
        user-select: none;
    }
`;

const SelectedNumber = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 2.5rem;
    width: 1rem;
    min-width: auto;
    height: 1rem;
    font-size: 0.75rem;
    line-height: 1rem;
    padding: 0;
    background-color: ${({ theme }) =>
        getThemeProps('palette.primary.gradientColor', DEFAULT_COLORS.BLUE, {
            theme,
        })};
    color: ${({ theme }) =>
        getThemeProps('palette.primary.text', DEFAULT_COLORS.BLACK, {
            theme,
        })};
    border-radius: 100%;
    top: 50%;
    transform: translateY(-50%);
    &.disabled {
        background-color: ${DEFAULT_COLORS.LIGHTLY};
        color: ${DEFAULT_COLORS.DARK};
    }
`;

const CheckboxItem = styled.div`
    align-items: start;
    display: flex;
    overflow-x: hidden;
    width: 100%;
    word-break: break-word;
    white-space: nowrap;
`;

const IndicatorContainer = styled.div`
    margin: 0.25rem -0.156rem 0 0;
    line-height: 1;
`;

const IdentifierPatch = styled.div`
    background: ${({ color }) => color || 'transparent'};
    height: 0.875rem;
    width: 0.875rem;
    margin: 0.225rem 1.5rem 0 auto;
`;

const CheckboxColumn = styled(Col)`
    padding: 0.57rem 0 0.31rem 1.13rem;
`;

const CheckboxOption = styled(Typography)`
    margin: 0 0 0 0.4rem;
    font-size: 0.875rem;
`;

const TooltipFilter = ({
    name,
    onChange,
    options,
    rowsPerColumn,
    showAllBadge,
    theme,
    title,
    value: filterValues,
    isMultipleSelection,
    applyNewTooltipStyle,
    isDisabled,
}) => {
    const menuEl = React.useRef(null);
    const dropdownIconRef = React.useRef(null);
    filterValues = (filterValues || []).filter(value => value !== null);

    useEffect(() => {
        const handleMenuClose = event => {
            const { current } = get(menuEl, 'current.menuContentRef', {});
            // Close the menu only if we click outside the menu's content.
            if (
                current &&
                event.target &&
                !current.contains(event.target) &&
                dropdownIconRef &&
                !dropdownIconRef.current.contains(event.target)
            ) {
                const selectedValues = _cloneDeep(filterValues);

                callFunc(onChange, name, selectedValues);
            }
        };

        addEventListenerOnRoot('click', handleMenuClose);
        return () => {
            removeEventListenerOnRoot('click', handleMenuClose);
        };
    }, [filterValues, name, onChange]);

    const renderHeader = () => {
        return (
            <TooltipFilterWrapper ref={dropdownIconRef}>
                <HeaderStyled className={isDisabled ? 'disabled' : null}>
                    <Typography
                        fontWeight="500"
                        color={`${DEFAULT_COLORS.BLACK} !important`}
                        type="documentFilter"
                    >
                        {title}
                    </Typography>
                    {showAllBadge && (
                        <Badge
                            margin="1px 0 0"
                            padding="0 0.5rem"
                            style={{
                                backgroundColor: getThemeProps(
                                    'palette.primary.gradientColor',
                                    DEFAULT_COLORS.BLUE,
                                    {
                                        theme,
                                    }
                                ),
                                color: getThemeProps('palette.primary.text', DEFAULT_COLORS.BLACK, {
                                    theme,
                                }),
                                fontFamily: 'Roboto',
                                fontSize: '0.75rem',
                                fontWeight: '400',
                                lineHeight: '1rem',
                                position: 'absolute',
                                right: '2.5rem',
                            }}
                        >
                            {TOOLTIP_FILTER_ALL_BADGE.LABEL_TEXT}
                        </Badge>
                    )}
                    {!showAllBadge && (
                        <SelectedNumber className={isDisabled ? 'disabled' : null}>
                            {filterValues.length}
                        </SelectedNumber>
                    )}
                    <IndicatorContainer>
                        <GridIcon fill={isDisabled ? DEFAULT_COLORS.DARK : DEFAULT_COLORS.BLACK} />
                    </IndicatorContainer>
                </HeaderStyled>
            </TooltipFilterWrapper>
        );
    };

    const handleOnMouseLeave = () => {
        const selectedValues = _cloneDeep(filterValues);

        callFunc(onChange, name, selectedValues);
    };

    const handleOnChange = inputId => {
        let selectedValues = _cloneDeep(filterValues);
        const allOptionId = options?.[0]?.id === 'all' ? 'all' : null;

        if (!isMultipleSelection) {
            if (selectedValues.indexOf(inputId) !== -1) {
                selectedValues = [];
            } else {
                selectedValues = [inputId];
            }
        } else if (inputId === allOptionId) {
            selectedValues = [allOptionId];
        } else if (filterValues.indexOf(inputId) === -1) {
            selectedValues = [...selectedValues, inputId];
        } else {
            selectedValues = selectedValues.filter(selectedValue => selectedValue !== inputId);
        }

        if (
            allOptionId &&
            filterValues.indexOf(allOptionId) === -1 &&
            (selectedValues.length === options.length - 1 || _isEmpty(selectedValues))
        ) {
            selectedValues = [allOptionId];
        }

        callFunc(onChange, name, selectedValues, true);
    };

    const renderCheckboxes = () => {
        if (isDisabled) {
            return '';
        }

        const newOptions = _chunk(options, rowsPerColumn);

        return (
            <Row>
                {newOptions.map(options => {
                    if (!Array.isArray(options)) {
                        return null;
                    }

                    const key = options.reduce((sum, option) => sum + option.id, 0);

                    return (
                        <CheckboxColumn key={key}>
                            {options.map(({ color, id: inputId, label }) => {
                                const onInputChange = () => {
                                    handleOnChange(inputId);
                                };
                                const text =
                                    label && FILTER_DOCUMENT_TYPES[label.toUpperCase()]
                                        ? FILTER_DOCUMENT_TYPES[label.toUpperCase()]
                                        : label;

                                return (
                                    <CheckboxItem key={inputId}>
                                        <Checkbox
                                            checked={filterValues.indexOf(inputId) !== -1}
                                            id={inputId}
                                            data-testid={`checkbox-${inputId}`}
                                            type="checkbox"
                                            icon={CheckboxEmptyIcon}
                                            checkedIcon={() => (
                                                <CheckboxFilledIcon width="19px" height="19px" />
                                            )}
                                            onChange={onInputChange}
                                        />
                                        <CheckboxOption
                                            as="label"
                                            htmlFor={inputId}
                                            type="documentCheckboxFilter"
                                        >
                                            {text}
                                        </CheckboxOption>
                                        {color && <IdentifierPatch color={color} />}
                                    </CheckboxItem>
                                );
                            })}
                        </CheckboxColumn>
                    );
                })}
            </Row>
        );
    };

    return (
        <ContentTooltipPreview
            onMouseLeave={handleOnMouseLeave}
            color={DEFAULT_COLORS.WHITE}
            borderColor={DEFAULT_COLORS.LIGHTEST}
            fontWeight=""
            hideTooltip
            offset={[]}
            placement="bottom-start"
            content={renderCheckboxes()}
            icon={renderHeader()}
            width="39.25rem"
            boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.05)"
            applyNewTooltipStyle={applyNewTooltipStyle}
        />
    );
};

TooltipFilter.propTypes = {
    isMultipleSelection: bool,
    name: string.isRequired,
    onChange: func,
    options: array,
    rowsPerColumn: number,
    showAllBadge: bool,
    theme: object.isRequired,
    title: string,
    value: array,
    applyNewTooltipStyle: bool,
    isDisabled: bool,
};

TooltipFilter.defaultProps = {
    isMultipleSelection: true,
    onChange: null,
    options: [],
    rowsPerColumn: 1,
    showAllBadge: false,
    title: null,
    value: [],
    applyNewTooltipStyle: true,
    isDisabled: false,
};

export { TooltipFilter as TooltipFilterUnwrapped };
export default withTheme(TooltipFilter);
